import { useCallback, useEffect, useRef, useState } from "react";
import {
  LoadingOverlay,
  Typography,
  Modal,
} from "@sunrun/experience-ui-components";
import { HttpStatusCode } from "axios";
import { LoginComponent } from "components/organisms/LoginComponent";
import { Message, postMessage } from "messages";
import {
  clearJwtCache,
  getJwtCache,
  postOktaCallbackErrorToOpener,
  postOktaCallbackToOpener,
  setJwtCache,
} from "services/jwtCache";
import { getHybridToken, verifyJwt } from "services/hybridAuthService";
import { getOktaAuth, getToken } from "services/oktaService";
import { logger } from "services/logger";
import { Mixpanel } from "providers/mixpanel";
import { AUTH__TOKEN_REFRESHED } from "constants/mixpanelEvents";
import { getTokenFromRefreshToken } from "services/splatAuthService";
import {
  clearRefreshTokenCache,
  getRefreshTokenCache,
  setRefreshTokenCache,
} from "services/refreshTokenCache";

type Mode = "INACTIVE" | "LOGIN" | "OKTA_CALLBACK" | "REFRESHING";
const oktaAuth = getOktaAuth();

const LoginPage = () => {
  const [mode, setMode] = useState<Mode>(
    oktaAuth.isLoginRedirect() ? "OKTA_CALLBACK" : "INACTIVE"
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const authValidationInFlight = useRef(false);

  const postCurrentToken = useCallback(() => {
    const cachedJwt = getJwtCache();
    if (!cachedJwt?.token) {
      return;
    }
    postMessage({
      type: "EVENT:AUTH_TOKEN",
      source: "Auth",
      payload: {
        tokenType: cachedJwt.tokenType,
        token: cachedJwt.token,
      },
    });
  }, []);

  const validateJwt = useCallback(async () => {
    const expireToken = () => {
      clearJwtCache();
      clearRefreshTokenCache();
      Mixpanel.reset();
      setMode("LOGIN");
      postMessage({
        type: "COMMAND:LOGOUT",
        source: "Auth",
      });
    };

    const attemptRefresh = async () => {
      // Tell everyone we're logged out
      postMessage({
        type: "COMMAND:LOGOUT",
        source: "Auth",
      });

      const cachedJwt = getJwtCache();
      try {
        setMode("REFRESHING");
        let newAccessToken;
        switch (cachedJwt?.tokenType) {
          case "okta":
            // TODO: Refresh Okta token
            expireToken();
            return;
          case "salesforce":
            const cachedRefreshToken = getRefreshTokenCache();
            if (!cachedRefreshToken) {
              expireToken();
              return;
            }
            const refreshResult = await getTokenFromRefreshToken(
              cachedRefreshToken
            );
            if (!refreshResult.success) {
              if (refreshResult.reason !== "refresh_token_expired") {
                logger.error(refreshResult.error);
              }
              expireToken();
              return;
            }
            newAccessToken = refreshResult.token;
            if (refreshResult.refreshToken) {
              setRefreshTokenCache(refreshResult.refreshToken);
            }
            break;
          default:
            expireToken();
            return;
        }
        const { hybridToken } = await getHybridToken(
          cachedJwt.tokenType,
          newAccessToken
        );
        setJwtCache({
          token: hybridToken,
          tokenType: cachedJwt.tokenType,
          source: "splat",
        });
        postMessage({
          type: "EVENT:AUTH_TOKEN",
          source: "Auth",
          payload: {
            tokenType: cachedJwt.tokenType,
            token: hybridToken,
          },
        });
        Mixpanel.track(AUTH__TOKEN_REFRESHED, {
          tokenType: cachedJwt.tokenType,
        });
        setMode("INACTIVE");
      } catch (e: any) {
        logger.error(e);
        expireToken();
      }
    };

    const cachedJwt = getJwtCache();
    if (!cachedJwt?.token) {
      expireToken();
      return;
    }
    try {
      if (
        cachedJwt.expiration &&
        cachedJwt.expiration < new Date().valueOf() / 1000
      ) {
        await attemptRefresh();
        return;
      }

      setMode("INACTIVE");
      await verifyJwt();
    } catch (e: any) {
      const code = e.response?.status;
      if (code === HttpStatusCode.Unauthorized) {
        await attemptRefresh();
      } else {
        logger.error(e);
      }
    }
  }, [setMode]);
  const messageHandler = useCallback(
    async (event: MessageEvent<Message>) => {
      // Ignore messages not sent by the ONE ecosystem
      if (!event?.data?.type) return;
      // Ignore messages that started from us
      if (event.data.source === "Auth") return;

      const message = event.data;
      switch (message.type) {
        case "COMMAND:LOGOUT": {
          clearJwtCache();
          clearRefreshTokenCache();
          Mixpanel.reset();
          setMode("LOGIN");
          break;
        }
        case "COMMAND:MASQUERADE": {
          const { masqueradeContactId } = message.payload;
          const cachedJwt = getJwtCache();
          if (!cachedJwt) {
            return;
          }
          const { hybridToken } = await getHybridToken(
            "masquerade",
            cachedJwt.token,
            masqueradeContactId
          );
          setJwtCache({
            token: hybridToken,
            tokenType: "okta",
            expiration: cachedJwt.expiration,
            source: "one",
          });
          postMessage({
            type: "EVENT:AUTH_TOKEN",
            source: "Auth",
            payload: {
              tokenType: "okta",
              token: hybridToken,
            },
          });
          break;
        }
        case "QUERY:AUTH_TOKEN": {
          // Always proactively reply with the current token, assuming it's going to be valid
          postCurrentToken();
          if (authValidationInFlight.current) {
            return;
          }
          authValidationInFlight.current = true;
          setMode("INACTIVE");
          try {
            await validateJwt();
            // If valid, re-post the token
            postCurrentToken();
          } finally {
            authValidationInFlight.current = false;
          }
          break;
        }
      }
    },
    [postCurrentToken, validateJwt]
  );
  useEffect(() => {
    window.addEventListener("message", messageHandler);
    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, [messageHandler]);
  const alreadyRanReady = useRef(false);
  useEffect(() => {
    if (alreadyRanReady.current) return;
    alreadyRanReady.current = true;
    postMessage({
      source: "Auth",
      type: "EVENT:READY",
    });
  }, [alreadyRanReady]);
  const alreadyRanCallback = useRef(false);
  const oktaCallback = useCallback(async () => {
    if (mode !== "OKTA_CALLBACK") return;
    try {
      const oktaTokenState = await getToken();
      const { hybridToken } = await getHybridToken(
        "okta",
        oktaTokenState.token
      );
      const tokenState = {
        token: hybridToken,
        tokenType: "okta" as const,
        expiration: oktaTokenState.expiration,
        source: "one" as const,
      };
      postOktaCallbackToOpener(tokenState);
      setJwtCache(tokenState);
      window.close();
    } catch (e: any) {
      logger.error(e);
      const errorMsg = `${e?.name || "Unknown Error"}: ${
        e?.message || "Unknown Message"
      }`;
      postOktaCallbackErrorToOpener(errorMsg);
      setErrorMessage(errorMsg);
      setMode("LOGIN");
    }
  }, [mode]);
  useEffect(() => {
    if (mode !== "OKTA_CALLBACK") return;
    if (alreadyRanCallback.current) return;
    alreadyRanCallback.current = true;
    oktaCallback();
  }, [mode, oktaCallback, alreadyRanCallback]);
  return (
    <>
      {(mode === "LOGIN" || mode === "REFRESHING") && <LoginComponent />}
      {mode === "OKTA_CALLBACK" && (
        <LoadingOverlay message="Creating Session..." />
      )}
      {mode === "REFRESHING" && (
        <LoadingOverlay message="Refreshing Session..." />
      )}
      {errorMessage && (
        <Modal
          testId="authentication-error-modal"
          title="Authentication Error"
          hideClose={true}
          type="error"
        >
          <Typography>{errorMessage}</Typography>
        </Modal>
      )}
    </>
  );
};

export { LoginPage };
